import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettingsCtx } from "@/context/SettingsContext";

const inputCls = "w-full rounded-xl border border-white/10 bg-neutral-900 px-4 py-3 text-sm text-white placeholder:text-neutral-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold";

const GROUPS = [
  {
    title: "Genel",
    fields: [
      { name: "brand_name", label: "Marka Adı" },
      { name: "tagline", label: "Slogan" },
      { name: "working_hours", label: "Çalışma Saatleri" },
    ],
  },
  {
    title: "İletişim",
    fields: [
      { name: "phone", label: "Telefon (görünen)" },
      { name: "phone_intl", label: "Telefon (uluslararası, tel: için)" },
      { name: "whatsapp", label: "WhatsApp Numarası" },
      { name: "whatsapp_default_message", label: "Varsayılan WhatsApp Mesajı", textarea: true },
      { name: "email", label: "E-posta" },
      { name: "address", label: "Adres" },
      { name: "maps_link", label: "Harita / Yol Tarifi Linki" },
    ],
  },
  {
    title: "Sosyal Medya",
    fields: [
      { name: "instagram", label: "Instagram URL" },
      { name: "facebook", label: "Facebook URL" },
    ],
  },
  {
    title: "SEO & Analytics",
    fields: [
      { name: "seo_title", label: "Varsayılan SEO Başlığı" },
      { name: "seo_description", label: "Varsayılan SEO Açıklaması", textarea: true },
      { name: "google_analytics_id", label: "Google Analytics ID" },
      { name: "google_search_console", label: "Search Console Doğrulama Kodu" },
    ],
  },
  {
    title: "Bakım Modu",
    note: "Bakım modu açıkken ziyaretçiler bakım ekranını görür. Giriş yapmış yöneticiler ve gizli önizleme linki (?onizleme=KOD) ile gelenler gerçek siteyi görür.",
    fields: [
      { name: "maintenance_mode", label: "Bakım Modu Açık", type: "toggle" },
      { name: "maintenance_title", label: "Bakım Sayfası Başlığı" },
      { name: "maintenance_message", label: "Bakım Sayfası Mesajı", textarea: true },
      { name: "maintenance_preview_code", label: "Önizleme Kodu (gizli link: ?onizleme=KOD)" },
      { name: "maintenance_show_whatsapp", label: "WhatsApp Butonunu Göster", type: "toggle" },
      { name: "maintenance_show_phone", label: "Telefon Butonunu Göster", type: "toggle" },
      { name: "maintenance_show_directions", label: "Yol Tarifi Butonunu Göster", type: "toggle" },
    ],
  },
];

export default function SettingsAdmin() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { patch } = useSettingsCtx();

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setData(data || {})).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function save() {
    setSaving(true);
    try {
      const { data: updated } = await api.put("/admin/settings", data);
      setData(updated);
      patch({
        maintenance_mode: !!updated.maintenance_mode,
        maintenance_title: updated.maintenance_title,
        maintenance_message: updated.maintenance_message,
        maintenance_show_whatsapp: updated.maintenance_show_whatsapp,
        maintenance_show_phone: updated.maintenance_show_phone,
        maintenance_show_directions: updated.maintenance_show_directions,
      });
      toast.success("Site ayarları kaydedildi.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="flex items-center justify-center py-16 text-neutral-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Yükleniyor…</div>;

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-medium text-white sm:text-3xl">Site Ayarları</h1>
          <p className="mt-1 text-sm text-neutral-500">İletişim, sosyal medya ve SEO bilgileri.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 rounded-xl bg-gold px-5 py-2.5 text-sm font-bold text-black hover:bg-gold-300 transition-colors disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet
        </button>
      </div>

      <div className="mt-8 space-y-6">
        {GROUPS.map((g) => (
          <div key={g.title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
            <h2 className="font-display text-lg font-medium text-white">{g.title}</h2>
            {g.note && <p className="mt-1.5 text-xs leading-relaxed text-neutral-500">{g.note}</p>}
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {g.fields.map((f) => (
                <div key={f.name} className={f.textarea || f.type === "toggle" ? "sm:col-span-2" : ""}>
                  {f.type === "toggle" ? (
                    <button
                      type="button"
                      role="switch"
                      aria-checked={!!data[f.name]}
                      onClick={() => setData((d) => ({ ...d, [f.name]: !d[f.name] }))}
                      data-testid={`settings-toggle-${f.name}`}
                      className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-neutral-900 px-4 py-3 text-left text-sm text-white"
                    >
                      <span className="font-medium">{f.label}</span>
                      <span className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${data[f.name] ? "bg-gold" : "bg-neutral-700"}`}>
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${data[f.name] ? "translate-x-5" : "translate-x-0.5"}`} />
                      </span>
                    </button>
                  ) : (
                    <>
                      <label className="mb-1.5 block text-xs font-medium text-neutral-400">{f.label}</label>
                      {f.textarea ? (
                        <textarea rows={2} className={inputCls} value={data[f.name] || ""} onChange={(e) => setData((d) => ({ ...d, [f.name]: e.target.value }))} />
                      ) : (
                        <input className={inputCls} value={data[f.name] || ""} onChange={(e) => setData((d) => ({ ...d, [f.name]: e.target.value }))} />
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
